// src/app/components/prompt-tracking/prompt-intent-group.tsx

import { ChevronDown, ChevronRight } from 'lucide-react';
import { PromptCard } from './prompt-card';
import type { IntentConfig } from '../../constants/prompt-tracking-constants';
import type { Prompt } from '../../types/prompt-tracking-types';

interface PromptIntentGroupProps {
  intentConfig: IntentConfig;
  prompts: Prompt[];
  isExpanded: boolean;
  onToggle: () => void;
  onAnalysisComplete: (id: string) => void;
  onDeleted: (id: string) => void;
}

export function PromptIntentGroup({
  intentConfig,
  prompts,
  isExpanded,
  onToggle,
  onAnalysisComplete,
  onDeleted,
}: PromptIntentGroupProps) {
  const Icon = intentConfig.icon;

  const analyzedPrompts = prompts.filter((p) => p.recommendationRate > 0);
  const avgRate =
    analyzedPrompts.length > 0
      ? (analyzedPrompts.reduce((s, p) => s + p.recommendationRate, 0) / analyzedPrompts.length).toFixed(0)
      : null;

  return (
    <div>
      {/* Group header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 p-3 md:p-4 rounded-lg transition-colors mb-3"
        style={{
          backgroundColor: 'var(--muted-30)',
          border: '1px solid var(--border)',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: intentConfig.bgColor }}
          >
            <Icon className="w-4 h-4" style={{ color: intentConfig.color }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3
                style={{
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--foreground)',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {intentConfig.label}
              </h3>
              <span
                className="px-2 py-0.5 rounded-md flex-shrink-0"
                style={{
                  backgroundColor: 'var(--muted)',
                  color: 'var(--muted-foreground)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-weight-semibold)',
                }}
              >
                {prompts.length}
              </span>
            </div>
            <p
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--muted-foreground)',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {intentConfig.description}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 md:gap-4 flex-shrink-0">
          {avgRate !== null && (
            <div className="flex items-baseline gap-1 md:gap-2">
              <span
                className="hidden sm:inline"
                style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}
              >
                Avg:
              </span>
              <span
                style={{
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--foreground)',
                }}
              >
                {avgRate}%
              </span>
            </div>
          )}
          {isExpanded
            ? <ChevronDown  className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--muted-foreground)' }} />
            : <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--muted-foreground)' }} />}
        </div>
      </button>

      {/* Cards */}
      {isExpanded && (
        <div className="space-y-3">
          {prompts.length > 0 ? (
            prompts.map((prompt) => (
              <PromptCard
                key={prompt.id}
                prompt={prompt}
                onAnalysisComplete={onAnalysisComplete}
                onDeleted={onDeleted}
              />
            ))
          ) : (
            <div
              className="p-6 md:p-8 text-center rounded-lg"
              style={{ backgroundColor: 'var(--muted-30)' }}
            >
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--muted-foreground)',
                  marginBottom: '6px',
                }}
              >
                No prompts yet for this intent.
              </p>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
                Click <strong>Add Prompt</strong> above to start tracking.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}